'use client';

import { ChevronDown, Search } from 'lucide-react';
import { type CSSProperties, type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './SelectInput.module.css';
import { normalizeForSearch } from '@/lib/search';

export interface SelectOption {
	value: string;
	label: string;
}

interface SelectInputProps {
	value: string;
	onChange: (value: string) => void;
	options: SelectOption[];
	disabled?: boolean;
	placeholder?: string;
	className?: string;
	hasError?: boolean;
	searchable?: boolean;
	renderOption?: (option: SelectOption, state: { selected: boolean }) => ReactNode;
	getOptionSearchText?: (option: SelectOption) => string;
	matchesSearch?: (option: SelectOption, query: string) => boolean;
	// Relevancia opcional: cuando se pasa, los resultados filtrados se ordenan por
	// score descendente (coincidencia literal/prefijo arriba, subsequence abajo),
	// igual que el buscador de investigadores. Sin esta prop el orden original se
	// respeta (comportamiento previo).
	scoreSearch?: (option: SelectOption, query: string) => number;
	/**
	 * Contenido propio para el disparador, en lugar del par valor + chevron.
	 *
	 * Existe para los filtros aplicados del directorio, donde el control es la
	 * etiqueta ENTERA ("Facultad · Ingeniería") y no sólo el valor: si el nombre del
	 * filtro quedara fuera del botón, tocarlo no abriría nada y el usuario
	 * descubriría por prueba y error qué parte del chip responde.
	 */
	renderTrigger?: (state: { selected: SelectOption | undefined; open: boolean }) => ReactNode;
	/** Ancho mínimo del menú, en px. Por defecto toma el del disparador; conviene
	 *  forzarlo cuando el disparador es angosto y las opciones no son cortas. */
	menuMinWidth?: number;
	/**
	 * Alto máximo del menú (cualquier medida CSS). Por defecto 280 px.
	 *
	 * Subirlo sirve para listas donde se quiere ver TODO de un vistazo —las 28
	 * unidades, por ejemplo— en vez de recorrer con la rueda. No se puede quitar
	 * del todo: sin tope, una lista larga se sale de la pantalla y las últimas
	 * opciones quedan inalcanzables. `70vh` es lo más que entra sin llegar a eso.
	 */
	menuMaxHeight?: string;
	/**
	 * Una línea pequeña y gris al tope del menú abierto, sobre el buscador y la lista ("2 en Facultad de
	 * Ciencias de la Salud"). Sirve para decir QUÉ alcance tiene la lista cuando otro filtro la recorta; no
	 * participa de la selección ni de la búsqueda.
	 */
	menuHint?: string;
	/** `id` of the trigger button, so a `<label htmlFor>` can name the control. */
	id?: string;
	/** Accessible name when no visible `<label>` exists (e.g. an "add" select with only a placeholder). */
	'aria-label'?: string;
	/** `id` of the element that names the control (a heading or label elsewhere). */
	'aria-labelledby'?: string;
}

interface MenuPosition {
	top: number;
	left: number;
	width: number;
}

export function SelectInput({
	value,
	onChange,
	options,
	disabled,
	placeholder = 'Seleccionar...',
	className,
	hasError,
	searchable = false,
	renderOption,
	getOptionSearchText,
	matchesSearch,
	scoreSearch,
	renderTrigger,
	menuMinWidth,
	menuMaxHeight,
	menuHint,
	id,
	'aria-label': ariaLabel,
	'aria-labelledby': ariaLabelledBy,
}: SelectInputProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
	const portalTarget = typeof document === 'undefined' ? null : document.body;
	const triggerRef = useRef<HTMLButtonElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);
	const searchInputRef = useRef<HTMLInputElement>(null);

	const closeMenu = () => {
		setIsOpen(false);
		setQuery('');
	};

	useLayoutEffect(() => {
		if (!isOpen) return;

		const updatePosition = () => {
			const trigger = triggerRef.current;
			if (!trigger) return;
			const rect = trigger.getBoundingClientRect();
			const margin = 4;
			// Alto real del menú una vez montado; estima 280px (max-height) en el
			// primer pase, antes de que exista el nodo.
			const menuHeight = menuRef.current?.offsetHeight ?? 280;
			const spaceBelow = window.innerHeight - rect.bottom;
			// Si no entra abajo y hay más espacio arriba, abre hacia arriba (flip).
			const openUp = spaceBelow < menuHeight + margin + 8 && rect.top > spaceBelow;
			// Un menú más ancho que su disparador (`menuMinWidth`) no debe salirse por la derecha: se
			// corre hacia la izquierda lo justo para que entre en la ventana.
			const menuWidth = Math.max(rect.width, menuMinWidth ?? 0);
			const maxLeft = window.innerWidth - menuWidth - 8;
			setMenuPosition({
				top: openUp ? Math.max(8, rect.top - menuHeight - margin) : rect.bottom + margin,
				left: Math.max(8, Math.min(rect.left, maxLeft)),
				width: rect.width,
			});
		};

		updatePosition();
		// Segundo pase tras montar el menú para usar su alto real (corrige el flip).
		const raf = requestAnimationFrame(updatePosition);
		window.addEventListener('scroll', updatePosition, true);
		window.addEventListener('resize', updatePosition);

		return () => {
			cancelAnimationFrame(raf);
			window.removeEventListener('scroll', updatePosition, true);
			window.removeEventListener('resize', updatePosition);
		};
	}, [isOpen, query, menuMinWidth]);

	useEffect(() => {
		if (isOpen && searchable) {
			requestAnimationFrame(() => searchInputRef.current?.focus());
		}
	}, [isOpen, searchable]);

	const filteredOptions = useMemo(() => {
		if (!searchable) return options;
		const rawQuery = query.trim();
		if (!rawQuery) return options;
		const q = normalizeForSearch(rawQuery);
		const filtered = options.filter((opt) => matchesSearch
			? matchesSearch(opt, rawQuery)
			: normalizeForSearch(getOptionSearchText?.(opt) ?? opt.label).includes(q));
		if (!scoreSearch) return filtered;
		// Orden estable por relevancia: mayor score primero, empates conservan el
		// orden original de `options`.
		return filtered
			.map((opt, index) => ({ opt, index, score: scoreSearch(opt, rawQuery) }))
			.sort((a, b) => b.score - a.score || a.index - b.index)
			.map((entry) => entry.opt);
	}, [getOptionSearchText, matchesSearch, scoreSearch, query, options, searchable]);

	const handleSelect = (val: string) => {
		onChange(val);
		closeMenu();
	};

	const handleTriggerClick = () => {
		if (disabled) return;
		setIsOpen((prev) => {
			if (prev) setQuery('');
			return !prev;
		});
	};

	const triggerClassName = [
		className,
		styles.trigger,
		hasError ? styles.triggerError : '',
		disabled ? styles.triggerDisabled : '',
	].filter(Boolean).join(' ');

	const selectedOption = options.find((opt) => opt.value === value);

	return (
		<>
			<button
				ref={triggerRef}
				id={id}
				type="button"
				className={triggerClassName}
				onClick={handleTriggerClick}
				disabled={disabled}
				aria-haspopup="listbox"
				aria-expanded={isOpen}
				aria-label={ariaLabel}
				aria-labelledby={ariaLabelledBy}
			>
				{renderTrigger ? (
					renderTrigger({ selected: selectedOption, open: isOpen })
				) : (
					<>
						<span className={selectedOption ? styles.triggerValue : styles.triggerPlaceholder}>
							{selectedOption ? selectedOption.label : placeholder}
						</span>
						<ChevronDown size={14} className={styles.triggerChevron} aria-hidden="true" />
					</>
				)}
			</button>

			{isOpen && portalTarget && menuPosition ? createPortal(
				<>
					<button
						type="button"
						className={styles.backdrop}
						onClick={closeMenu}
						aria-label="Cerrar selector"
					/>
					<div
						ref={menuRef}
						className={styles.menu}
						role="listbox"
						style={{
							top: `${menuPosition.top}px`,
							left: `${menuPosition.left}px`,
							width: `${Math.max(menuPosition.width, menuMinWidth ?? 0)}px`,
							...(menuMaxHeight
								? ({
										'--select-menu-max-height': menuMaxHeight,
										'--select-menu-list-max-height': menuMaxHeight,
									} as CSSProperties)
								: {}),
						}}
					>
						{menuHint ? (
							<div className={styles.menuHint} role="presentation">
								{menuHint}
							</div>
						) : null}
						{searchable && (
							<div className={styles.searchRow}>
								<Search size={13} className={styles.searchIcon} aria-hidden="true" />
								<input
									ref={searchInputRef}
									type="text"
									className={styles.searchInput}
									placeholder="Buscar…"
									value={query}
									onChange={(event) => setQuery(event.target.value)}
									autoComplete="off"
								/>
							</div>
						)}
						<ul className={styles.optionList} role="presentation">
							{filteredOptions.length === 0 ? (
								<li className={styles.emptyHint}>Sin resultados</li>
							) : (
								filteredOptions.map((opt) => {
									const isSelected = opt.value === value;
									return (
										<li key={opt.value}>
											<button
												type="button"
												className={`${styles.option} ${isSelected ? styles.optionSelected : ''}`}
												role="option"
												aria-selected={isSelected}
												onClick={() => handleSelect(opt.value)}
											>
												{renderOption ? renderOption(opt, { selected: isSelected }) : opt.label}
											</button>
										</li>
									);
								})
							)}
						</ul>
					</div>
				</>,
				portalTarget,
			) : null}
		</>
	);
}
